import { useCallback, useEffect, useRef, useState } from 'react';

// Small data-loading hook. `key` identifies the request (null = do not load), `load(key)` returns a promise.
// While a new key loads, the previous data stays available (`loading` is true), so lists do not flash.
// `group` (optional) names what the data belongs to, e.g. the list tab: data of ANOTHER group is never shown while the
// new one loads (`data` is null, `loaded` false), so switching a tab does not show the old tab's rows for a moment.
// `error` belongs to the current request only. `reload()` repeats it, `mutate(fn)` edits the data locally.
export function useRemote(key, load, group) {
  const [version, setVersion] = useState(0);
  const [res, setRes] = useState({ stamp: null, data: null, error: null, group: undefined });
  const loadRef = useRef(load);
  const groupRef = useRef(group);

  useEffect(() => {
    loadRef.current = load;
    groupRef.current = group;
  });

  const stamp = key == null ? null : `${key}#${version}`;

  useEffect(() => {
    if (key == null) return undefined;
    let cancelled = false;
    const mine = `${key}#${version}`;
    const myGroup = groupRef.current;
    loadRef.current(key)
      .then((data) => { if (!cancelled) setRes({ stamp: mine, data, error: null, group: myGroup }); })
      .catch((error) => { if (!cancelled) setRes((prev) => ({ stamp: mine, data: prev.data, error, group: myGroup })); });
    return () => { cancelled = true; };
  }, [key, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const mutate = useCallback((fn) => setRes((prev) => ({ ...prev, data: fn(prev.data) })), []);

  const settled = stamp !== null && res.stamp === stamp;
  const sameGroup = group === undefined || res.group === group;
  return {
    data: sameGroup ? res.data : null,
    error: settled ? res.error : null,
    loading: stamp !== null && !settled,
    loaded: res.stamp !== null && sameGroup,
    reload,
    mutate,
  };
}
